import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { AcaraIcon } from './AcaraIcon'
import type { AcaraIconName } from './iconCatalog'
import { EmptyState as ProductEmptyState } from './product'

export type AcaraTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'destructive'
  icon?: AcaraIconName
}

export function AcaraButton({ variant = 'primary', icon, children, className = '', ...props }: ButtonProps) {
  const shell = variant === 'primary' ? 'button primary' : variant === 'secondary' ? 'button secondary' : 'button'
  return <button className={`acara-button acara-button--${variant} ${shell} ${className}`.trim()} {...props}>{icon ? <AcaraIcon name={icon} size={20} /> : null}<span>{children}</span></button>
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string
  error?: string
}

export function AcaraField({ label, hint, error, id, className = '', ...props }: FieldProps) {
  const fieldId = id ?? `acara-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  const helpId = `${fieldId}-help`
  return <label className={`acara-field ${className}`.trim()} htmlFor={fieldId}><span className="acara-field__label">{label}</span><input id={fieldId} aria-describedby={hint || error ? helpId : undefined} aria-invalid={error ? true : undefined} {...props} />{hint || error ? <small id={helpId} className={error ? 'acara-field__error' : 'acara-field__hint'}>{error ?? hint}</small> : null}</label>
}

const toneIcons: Record<AcaraTone, AcaraIconName> = {
  neutral: 'clock',
  info: 'notification',
  success: 'verified',
  warning: 'warning',
  danger: 'critical',
}

export function StatusChip({ tone = 'neutral', label }: { tone?: AcaraTone; label: string }) {
  return <span className={`acara-status acara-tone--${tone}`}><AcaraIcon name={toneIcons[tone]} size={16} /><span>{label}</span></span>
}

export function Notice({ tone = 'info', title, children, action }: { tone?: AcaraTone; title: string; children: ReactNode; action?: ReactNode }) {
  return <section className={`acara-notice acara-tone--${tone}`} aria-label={`${title} notice`}><span className="acara-notice__icon"><AcaraIcon name={toneIcons[tone]} size={24} /></span><div><h3>{title}</h3><div className="acara-notice__body">{children}</div>{action ? <div className="acara-notice__action">{action}</div> : null}</div></section>
}

export function KpiCard({ label, value, context, updatedAt, icon = 'analytics' }: { label: string; value: string; context: string; updatedAt?: string; icon?: AcaraIconName }) {
  return <article className="acara-kpi"><div className="acara-kpi__icon"><AcaraIcon name={icon} size={24} /></div><p>{label}</p><strong>{value}</strong><span>{context}</span>{updatedAt ? <time>{updatedAt}</time> : null}</article>
}

export function EmptyState({ title, explanation, action, icon = 'events' }: { title: string; explanation: string; action?: ReactNode; icon?: AcaraIconName }) {
  return <ProductEmptyState icon={icon} title={title} description={explanation} action={action} />
}

export function CommandPanel({ title, authority, impact, children, result }: { title: string; authority: string; impact: string; children: ReactNode; result?: string }) {
  const chip = authority.includes('.') ? 'Needs step-up' : authority === 'self' ? 'Your account' : authority
  return <section className="acara-command" title={authority}><div className="acara-command__heading"><div><p className="acara-overline">Action</p><h3>{title}</h3></div><StatusChip tone="warning" label={chip} /></div><p>{impact}</p><div className="acara-command__body">{children}</div>{result ? <div className="acara-command__evidence"><AcaraIcon name="audit-log" size={20} /><span>{result}</span></div> : null}</section>
}

